import type { getDefaultStore } from 'jotai'

import { type LinkingColor, linkingColorsAtomFamily } from '@/state/linking'
import {
  DEFAULT_ORDER_FORM_CARD_ID,
  lastFocusedOrderCardAtom,
  registeredOrderContainersAtom,
} from '@/state/order'

type JotaiStore = ReturnType<typeof getDefaultStore>

const ORDER_GROUP_TICKET_PREFIX = 'order-group:'

/**
 * The parked ticket of a link group with no Order Form in it: rows its widgets add land
 * here (shown by the popup sheet), and a form that moves into the group picks them up —
 * see useOrderFormGroupSwitch. Per page, as link colors are.
 */
export function orderGroupTicketId(pageId: string, color: LinkingColor): string {
  return `${ORDER_GROUP_TICKET_PREFIX}${pageId}:${color}`
}

/** An id that is an Order Form surface itself: the popup sheet's own ticket or a group's. */
function isOrderFormSurfaceId(id: string): boolean {
  return (
    id === DEFAULT_ORDER_FORM_CARD_ID ||
    id.startsWith(ORDER_GROUP_TICKET_PREFIX) ||
    id.startsWith('tutorial-preview-')
  )
}

/**
 * Resolves which Order Form card ID should receive writes/reads (selection, orderbook
 * signals, form values, etc.) for a widget `sourceCardId`.
 *
 * Rows only move within a link group: a widget in the pink group adds to the pink group's
 * ticket, never another group's. In order:
 *
 * 1. The source is itself a mounted Order Form, or a ticket the popup sheet shows (its own
 *    `DEFAULT_ORDER_FORM_CARD_ID`, or a group ticket) → that same id.
 * 2. The source belongs to a link group (`linkingColors`, its page's card → color map) →
 *    that group's form: the last-focused one if it's in the group, else the first mounted.
 *    No form in the group → the group's parked ticket (orderGroupTicketId), never some
 *    other group's form.
 * 3. Unlinked, or not a dashboard widget (chat, commands) → the last-focused mounted form,
 *    else any mounted one, else `DEFAULT_ORDER_FORM_CARD_ID` (the popup's own ticket).
 *    Callers wanting "whichever form" must not pass a surface id (rule 1); they pass
 *    ANY_ORDER_FORM_SOURCE.
 *
 * Exported as a pure function (`computeOrderFormTarget`) so the read side
 * (`useOrderFormTargetId`/`useOrderFormSelectionTarget`) and write side
 * (`resolveOrderFormTarget`, used by `useOrderFormDispatch`/`useOrderFormPopup`) can
 * never diverge — they MUST agree on the same target, or a widget's read can silently
 * disagree with where writes land (e.g. a toggle-off/side-flip click reads an empty
 * fallback atom, decides nothing is selected yet, and re-adds instead of
 * removing/flipping).
 */
export function computeOrderFormTarget(
  lastFocused: string | null,
  registered: Set<string>,
  sourceCardId: string,
  linkingColors: Record<string, LinkingColor> = {},
  pageId?: string
): string {
  if (isOrderFormSurfaceId(sourceCardId) || registered.has(sourceCardId)) return sourceCardId
  const group = linkingColors[sourceCardId]
  if (group && group !== 'unlinked') {
    const inGroup = [...registered].filter((id) => orderFormGroup(linkingColors, id) === group)
    if (lastFocused && inGroup.includes(lastFocused)) return lastFocused
    if (inGroup.length > 0) return inGroup[0]
    return pageId ? orderGroupTicketId(pageId, group) : DEFAULT_ORDER_FORM_CARD_ID
  }
  if (lastFocused && registered.has(lastFocused)) return lastFocused
  if (registered.size > 0) return registered.values().next().value as string
  return DEFAULT_ORDER_FORM_CARD_ID
}

/**
 * An Order Form's link group. One without an entry yet, or saved as unlinked before the form
 * lost that choice, is mint (see LinkingActionHeader's allowUnlinked).
 */
export function orderFormGroup(
  linkingColors: Record<string, LinkingColor>,
  formCardId: string
): Exclude<LinkingColor, 'unlinked'> {
  const color = linkingColors[formCardId]
  return !color || color === 'unlinked' ? 'mint' : color
}

/** A source id for callers that want whichever form is focused/mounted (rule 3). */
export const ANY_ORDER_FORM_SOURCE = 'any-order-form'

/** `pageId` is the source widget's dashboard page (LinkingPageContext); omit it outside one. */
export function resolveOrderFormTarget(
  store: JotaiStore,
  sourceCardId: string,
  pageId?: string
): string {
  const lastFocused = store.get(lastFocusedOrderCardAtom)
  const registered = store.get(registeredOrderContainersAtom)
  const linkingColors = pageId ? store.get(linkingColorsAtomFamily(pageId)) : undefined
  return computeOrderFormTarget(lastFocused, registered, sourceCardId, linkingColors, pageId)
}
